import React, {useContext, useState, useCallback, useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import {UpdatePartnerRequest} from '@common/models'

import {
  ButtonContainer,
  FormContainer,
  FormField,
  InfoContainer,
  Label,
  RequiredIndicator,
} from './PartnerProfilePage.styled'

import {Container} from '~/components/general/container/Container'
import {Typography} from '~/components/general/typography/Typography'
import {Input} from '~/components/general/input/Input'
import {Button} from '~/components/general/button/Button'
import {Card} from '~/components/general/card/Card'
import {Layout} from '~/components/layout/Layout'
import {LoadingPage} from '~/components/website/LoadingPage'
import {PartnerAdministratorContext} from '~/components/profile/PartnerAdministratorContext'
import {useUpdatePartnerAdministratorPartner} from '~/api/partnerAdministrator'
import {isEmailValid} from '~/utils/general'
import {
  validatePhoneNumberInput,
  translatePhoneValidationError,
} from '~/utils/phoneValidation'
import {
  NotificationContext,
  NotificationType,
} from '~/components/notifications/NotificationContext'

export const PartnerProfilePage: React.FC = () => {
  const {t} = useTranslation<string>()
  const {partner, partnerAdministrator, isLoading} = useContext(
    PartnerAdministratorContext,
  )
  const {addNotification} = useContext(NotificationContext)
  const [isEditing, setIsEditing] = useState(false)

  // Form state
  const [formData, setFormData] = useState<UpdatePartnerRequest>({
    name: '',
    logoUrl: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    termsAndConditionsUrl: '',
    privacyPolicyUrl: '',
  })

  // Form errors
  const [errors, setErrors] = useState<
    Partial<Record<keyof UpdatePartnerRequest, string>>
  >({})

  const {mutate: updatePartner, isPending: isUpdating} =
    useUpdatePartnerAdministratorPartner()

  // Initialize form data when partner data loads
  useEffect(() => {
    if (partner) {
      setFormData({
        name: partner.name || '',
        logoUrl: partner.logoUrl || '',
        address: partner.address || '',
        phone: partner.phone || '',
        email: partner.email || '',
        website: partner.website || '',
        termsAndConditionsUrl: partner.termsAndConditionsUrl || '',
        privacyPolicyUrl: partner.privacyPolicyUrl || '',
      })
    }
  }, [partner])

  const handleInputChange = useCallback(
    (field: keyof UpdatePartnerRequest) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const {value} = event.target
        setFormData((prev) => ({...prev, [field]: value}))

        // Clear error when user starts typing
        if (errors[field]) {
          setErrors((prev) => ({...prev, [field]: undefined}))
        }
      },
    [errors],
  )

  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<Record<keyof UpdatePartnerRequest, string>> = {}

    if (!formData.name || formData.name.trim().length === 0) {
      newErrors.name = t('Partner name is required')
    } else if (formData.name.length > 255) {
      newErrors.name = t('Partner name must be 255 characters or less')
    }

    if (!isEmailValid(formData.email)) {
      newErrors.email = t('Please enter a valid email address')
    }
    const phoneValidation = validatePhoneNumberInput(formData.phone)
    if (!phoneValidation.isValid && phoneValidation.error) {
      newErrors.phone = translatePhoneValidationError(
        phoneValidation.error.code,
        t,
      )
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [formData, t])

  const savePartnerData = useCallback(() => {
    if (!validateForm()) {
      return
    }

    updatePartner(formData, {
      onSuccess: () => {
        addNotification(
          t('Partner information updated successfully'),
          NotificationType.Success,
        )
        setIsEditing(false)
      },
      onError: () => {
        addNotification(
          t('Failed to update partner information'),
          NotificationType.Error,
        )
      },
    })
  }, [formData, validateForm, updatePartner, addNotification, t])

  const handleCancel = useCallback(() => {
    // Reset form to original values
    if (partner) {
      setFormData({
        name: partner.name || '',
        logoUrl: partner.logoUrl || '',
        address: partner.address || '',
        phone: partner.phone || '',
        email: partner.email || '',
        website: partner.website || '',
        termsAndConditionsUrl: partner.termsAndConditionsUrl || '',
        privacyPolicyUrl: partner.privacyPolicyUrl || '',
      })
    }
    setErrors({})
    setIsEditing(false)
  }, [partner])

  if (isLoading) {
    return <LoadingPage />
  }

  if (!partner || !partnerAdministrator) {
    return (
      <Layout>
        <Container>
          <Typography size="xl" weight="bold">
            {t('Partner Profile')}
          </Typography>
          <Typography>{t('Unable to load partner information.')}</Typography>
        </Container>
      </Layout>
    )
  }

  return (
    <Layout>
      <Container>
        <Typography size="xl" weight="bold">
          {t('Partner Profile')}
        </Typography>

        <InfoContainer>
          <Card>
            <Typography size="lg" weight="medium">
              {t('Administrator Information')}
            </Typography>
            <Typography>
              <strong>{t('Name')}:</strong> {partnerAdministrator.firstName}{' '}
              {partnerAdministrator.secondName}
            </Typography>
            <Typography>
              <strong>{t('Email')}:</strong> {partnerAdministrator.email}
            </Typography>
          </Card>
        </InfoContainer>

        <Card>
          <Typography size="lg" weight="medium">
            {t('Partner Information')}
          </Typography>

          <FormContainer>
            <Container flex direction="column" spacing={3}>
              <FormField>
                <Label>
                  {t('Partner Name')} <RequiredIndicator>*</RequiredIndicator>
                </Label>
                <Input
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  errorMessage={errors.name}
                  disabled={!isEditing}
                  required
                />
              </FormField>

              <FormField>
                <Label>{t('Logo URL')}</Label>
                <Input
                  value={formData.logoUrl}
                  onChange={handleInputChange('logoUrl')}
                  errorMessage={errors.logoUrl}
                  disabled={!isEditing}
                  placeholder="https://example.com/logo.png"
                />
              </FormField>

              <FormField>
                <Label>{t('Address')}</Label>
                <Input
                  value={formData.address}
                  onChange={handleInputChange('address')}
                  errorMessage={errors.address}
                  disabled={!isEditing}
                />
              </FormField>

              <FormField>
                <Label>{t('Phone')}</Label>
                <Input
                  value={formData.phone}
                  onChange={handleInputChange('phone')}
                  errorMessage={errors.phone}
                  disabled={!isEditing}
                />
              </FormField>

              <FormField>
                <Label>{t('Email')}</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  errorMessage={errors.email}
                  disabled={!isEditing}
                />
              </FormField>

              <FormField>
                <Label>{t('Website')}</Label>
                <Input
                  value={formData.website}
                  onChange={handleInputChange('website')}
                  errorMessage={errors.website}
                  disabled={!isEditing}
                  placeholder="https://example.com"
                />
              </FormField>

              <FormField>
                <Label>{t('Terms and Conditions URL')}</Label>
                <Input
                  value={formData.termsAndConditionsUrl}
                  onChange={handleInputChange('termsAndConditionsUrl')}
                  errorMessage={errors.termsAndConditionsUrl}
                  disabled={!isEditing}
                  placeholder="https://example.com/terms"
                />
              </FormField>

              <FormField>
                <Label>{t('Privacy Policy URL')}</Label>
                <Input
                  value={formData.privacyPolicyUrl}
                  onChange={handleInputChange('privacyPolicyUrl')}
                  errorMessage={errors.privacyPolicyUrl}
                  disabled={!isEditing}
                  placeholder="https://example.com/privacy"
                />
              </FormField>
            </Container>

            <ButtonContainer>
              {!isEditing ? (
                <Button onClick={() => setIsEditing(true)}>
                  {t('Edit Partner Information')}
                </Button>
              ) : (
                <>
                  <Button
                    color="secondary"
                    onClick={handleCancel}
                    disabled={isUpdating}>
                    {t('Cancel')}
                  </Button>
                  <Button
                    color="primary"
                    onClick={savePartnerData}
                    disabled={isUpdating}>
                    {isUpdating ? t('Saving...') : t('Save Changes')}
                  </Button>
                </>
              )}
            </ButtonContainer>
          </FormContainer>
        </Card>
      </Container>
    </Layout>
  )
}
